import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import { FormProvider, useForm } from "react-hook-form";
import ActionButton from "controls/global/action-button/ActionButton";
import AgencyLocationField from "pages/components/AgencyLocationField";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { usePropertyTypeOptions } from "utils/custom-hooks/usePropertyTypeOptions";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { usePolicyTypeOptions } from "utils/custom-hooks/usePolicyTypeOptions";
import { useStateList } from "utils/custom-hooks/useStateList";
import {
  EDIT_FIELD_GROUPS,
  EDIT_FIELDS_BY_GROUP,
  EditField,
  initialValueForField,
} from "./editFields";

const DialogHeading = styled(DialogTitle)({
  fontSize: fontSize.xlarge,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
});

const Subtitle = styled(Typography)({
  fontSize: fontSize.xsmall,
  color: colors.grey11,
  marginTop: "-12px",
  marginBottom: gaps.small1,
  paddingLeft: padding.large1,
  paddingRight: padding.large1,
});

const GroupTitle = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginTop: gaps.medium1,
  marginBottom: gaps.small1,
});

const FieldsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: gaps.medium1,
  "@media (max-width: 600px)": {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
});

const FieldLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold1,
  color: colors.grey11,
  marginBottom: gaps.xsmall2,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

const NoteSection = styled(Box)({
  marginTop: gaps.large1,
  paddingTop: gaps.medium1,
  borderTop: `1px solid ${colors.grey09}`,
});

const NoteHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: gaps.xsmall2,
});

const MAX_NOTE_CHARS = 1000;

interface EditRecordModalProps {
  open: boolean;
  onClose: () => void;
  record: PolicyManagementRecord | null;
  onApply?: (changes: Array<{ key: string; value: string }>, note: string) => void;
}

// Single-record edit dialog: every field in the EDIT_FIELDS catalog, already
// populated with this record's current value and independently editable —
// unlike the multi-record Bulk Edit modal (EditFieldsModal), there's no
// "pick a field, then add a row" step since there's only one record here.
const EditRecordModal = ({ open, onClose, record, onApply }: EditRecordModalProps) => {
  const methods = useForm({ mode: "onBlur" });

  const propertyTypeOptions = usePropertyTypeOptions();
  const recordTypeOptions = useRecordTypeOptions();
  const policyTypeOptions = usePolicyTypeOptions();
  const { stateList } = useStateList();
  const stateOptions = useMemo<OptionRenderType[]>(
    () => stateList.map((s) => ({ label: s.label, value: s.value })),
    [stateList]
  );

  const getSelectOptions = (field: EditField): OptionRenderType[] => {
    if (field.optionsSource === "propertyType") return propertyTypeOptions;
    if (field.optionsSource === "recordType") return recordTypeOptions;
    if (field.optionsSource === "policyType") return policyTypeOptions;
    if (field.optionsSource === "state") return stateOptions;
    return (field.options || []).map((opt) => ({ label: opt, value: opt }));
  };

  const [initialValues, setInitialValues] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [location, setLocationState] = useState<{
    value: string;
    label: string;
    companyId?: string;
    companyName?: string;
    companyLegacyID?: string;
    locationAgencyCompanyID?: string;
    locationReportingID?: string;
  } | null>(null);
  const [initialLocationValue, setInitialLocationValue] = useState("");
  const [note, setNote] = useState("");

  // Re-seed every field from the record each time the dialog opens fresh.
  useEffect(() => {
    if (!open || !record) return;
    const seeded: Record<string, string> = {};
    EDIT_FIELD_GROUPS.forEach((group) => {
      EDIT_FIELDS_BY_GROUP[group].forEach((f) => {
        if (f.type === "agencyLocation") return;
        seeded[f.key] = initialValueForField(
          record as unknown as Record<string, unknown>,
          f.key
        );
      });
    });
    setInitialValues(seeded);
    setValues(seeded);
    // Agency Location isn't stored on the record itself (only on the
    // Transfer payload), so — same as the Bulk Edit modal — it starts unset
    // and only counts as a change once the user explicitly picks one.
    setInitialLocationValue("");
    setLocationState(null);
    setNote("");
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record]);

  const setValue = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const setLocation = (loc: CompanyLocation | string) => {
    if (!loc || typeof loc === "string") return;
    setLocationState({
      value: loc.locationUniqueID ?? "",
      label: loc.locationDisplayName ?? "",
      companyId: loc.companyID ?? "",
      companyName: loc.companyName ?? "",
      companyLegacyID: loc.companyLegacyID ?? "",
      locationAgencyCompanyID: loc.locationAgencyCompanyID ?? "",
      locationReportingID: loc.locationReportingID ?? "",
    });
  };

  const clearLocation = () => {
    methods.setValue("agencyLocation", "");
    setLocationState({ value: "", label: "" });
  };

  const locationChanged = (location?.value ?? "") !== initialLocationValue;
  const changedKeys = Object.keys(values).filter(
    (key) => values[key] !== initialValues[key]
  );
  const fieldsCount = changedKeys.length + (locationChanged ? 1 : 0);
  const hasNote = note.trim().length > 0;
  const canApply = fieldsCount > 0 || hasNote;

  const handleApply = () => {
    if (!canApply) return;
    const changes: Array<{ key: string; value: string }> = changedKeys.map((key) => ({
      key,
      value: values[key],
    }));
    if (locationChanged && location) {
      changes.push(
        { key: "CompanyID", value: location.companyId ?? "" },
        { key: "CompanyName", value: location.companyName ?? "" },
        { key: "CompanyLegacyID", value: location.companyLegacyID ?? "" },
        { key: "LocationUniqueID", value: location.value },
        { key: "LocationDisplayName", value: location.label ?? "" },
        { key: "LocationReportingID", value: location.locationReportingID ?? "" },
        { key: "LocationAgencyCompanyID", value: location.locationAgencyCompanyID ?? "" }
      );
    }
    onApply?.(changes, note.trim());
    onClose();
  };

  const renderControl = (field: EditField) => {
    if (field.type === "agencyLocation") {
      return (
        <AgencyLocationField
          label=""
          name="agencyLocation"
          size="small"
          locationUniqueID={location?.value}
          onChange={setLocation}
          onClear={clearLocation}
        />
      );
    }
    const value = values[field.key] ?? "";
    switch (field.type) {
      case "select":
        return (
          <Select
            fullWidth
            size="small"
            value={value}
            onChange={(e) => setValue(field.key, e.target.value as string)}
            displayEmpty
          >
            <MenuItem value="">- Select -</MenuItem>
            {getSelectOptions(field).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        );
      case "date":
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={value}
            onChange={(e) => setValue(field.key, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case "dollar":
        return (
          <TextField
            fullWidth
            size="small"
            type="text"
            value={value}
            placeholder="0.00"
            onChange={(e) => setValue(field.key, e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        );
      case "text":
      default:
        return (
          <TextField
            fullWidth
            size="small"
            type="text"
            value={value}
            placeholder={field.placeholder || ""}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogHeading>
        <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1 }}>
          <EditNoteIcon sx={{ color: colors.blue01 }} />
          <span>Edit Record</span>
        </Box>
      </DialogHeading>
      <Subtitle>Policy #: {record?.PolicyNumber ?? "---"}</Subtitle>
      <DialogContent>
        <FormProvider {...methods}>
          <Box>
            {EDIT_FIELD_GROUPS.map((group) => (
              <Box key={group}>
                <GroupTitle>{group}</GroupTitle>
                <FieldsGrid>
                  {EDIT_FIELDS_BY_GROUP[group].map((field) => (
                    <Box key={field.key}>
                      <FieldLabel>{field.label}</FieldLabel>
                      {renderControl(field)}
                    </Box>
                  ))}
                </FieldsGrid>
              </Box>
            ))}

            <NoteSection>
              <NoteHeader>
                <Typography
                  sx={{
                    fontSize: fontSize.xxsmall,
                    fontWeight: fontWeight.bold2,
                    color: colors.grey11,
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}
                >
                  Note{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: "none",
                      letterSpacing: 0,
                      color: colors.grey11,
                    }}
                  >
                    (optional)
                  </span>
                </Typography>
                <Typography sx={{ fontSize: fontSize.xxsmall, color: colors.grey11 }}>
                  {note.length} / {MAX_NOTE_CHARS} characters
                </Typography>
              </NoteHeader>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                placeholder="Write a note that will be added to this record."
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_CHARS))}
                inputProps={{ maxLength: MAX_NOTE_CHARS }}
              />
            </NoteSection>
          </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11, marginRight: "auto" }}>
          {fieldsCount} field{fieldsCount === 1 ? "" : "s"} changed
        </Typography>
        <ActionButton variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          disabled={!canApply}
          onClick={handleApply}
        >
          Save Changes
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditRecordModal;
